/**
 * Opening, running and closing a shift when the network may not be there.
 *
 * Same pessimistic order as a sale — write locally, queue the upload, *then*
 * try the network — and for the same reason: a drawer counted at six in the
 * evening must not depend on a signal being there at six in the evening.
 *
 * The one thing this file does that the till does not is **mint the shift id**.
 * A shift opened with no signal has to be addressable by the cash movements and
 * the cash-up that follow it, hours before any of them have uploaded. If the id
 * came from the server, the queue would have nothing to name the shift it was
 * closing. Ordering in the outbox guarantees the open uploads first.
 *
 * What is never computed here: the expected cash and the variance. The server
 * works both out from the sales ledger at the moment the cash-up lands, which is
 * also how a sale that arrives late from another terminal still counts.
 */

import { ApiError, NetworkError } from "@/api/client";
import { db } from "@/db/database";
import type { LocalShift } from "@/db/schema";
import { authRepository } from "@/repositories/authRepository";
import { outboxRepository } from "@/repositories/outboxRepository";
import {
  shiftsRepository,
  type CashMovementType,
  type Shift,
} from "@/repositories/shiftsRepository";

export interface OpenShiftInput {
  tillId: string;
  openingFloat: string;
  cashierName: string;
}

export interface CashUpInput {
  shiftId: string;
  countedCash: string;
  note?: string;
}

export interface CashMovementInput {
  shiftId: string;
  movementType: CashMovementType;
  amount: string;
  reason: string;
}

export interface ShiftOutcome {
  shift: Shift | null;
  /** True when the operation is on this device only, waiting to upload. */
  queued: boolean;
}

export const localShiftRepository = {
  /**
   * Start trading.
   *
   * Returns the server's shift when it answers, and a locally-shaped one when it
   * does not — the cashier can start selling either way, and the sales they ring
   * up attach to the shift when both have uploaded.
   */
  async open(input: OpenShiftInput): Promise<ShiftOutcome> {
    const shiftId = crypto.randomUUID();
    const openedAt = new Date().toISOString();
    const businessDate = openedAt.slice(0, 10);
    const idempotencyKey = crypto.randomUUID();

    const cashierId = await authRepository.cashierId();
    const payload = {
      ...(cashierId ? { cashier_id: cashierId } : {}),
      till_id: input.tillId,
      shift_id: shiftId,
      opening_float: input.openingFloat,
      opened_at: openedAt,
    };

    const local: LocalShift = {
      id: shiftId,
      tillId: input.tillId,
      cashierName: input.cashierName,
      businessDate,
      openedAt,
      closedAt: null,
      openingFloat: input.openingFloat,
      countedCash: null,
      status: "OPEN",
      syncState: "PENDING",
    };

    // Both writes or neither. A shift stored but never queued would look open on
    // this terminal forever while the server had never heard of it, and every
    // sale rung up under it would attach to nothing.
    let operationId = "";
    await db.transaction("rw", db.shifts, db.outbox, db.syncMeta, async () => {
      await db.shifts.put(local);
      const operation = await outboxRepository.enqueue({
        type: "shift.open",
        entityId: shiftId,
        idempotencyKey,
        payload,
        businessDate,
      });
      operationId = operation.opId;
    });

    try {
      const shift = await shiftsRepository.open(payload, idempotencyKey);
      await db.shifts.update(shiftId, { syncState: "SYNCED" });
      // Out of the queue: the server has it. See the note in
      // `localSaleRepository` — leaving it queued uploads everything twice.
      await outboxRepository.acknowledge(operationId);
      return { shift, queued: false };
    } catch (error) {
      if (isTransient(error)) return { shift: offlineShift(local), queued: true };

      // A refusal, not a bad line — most often "that till already has a shift
      // open", because somebody opened one on the other terminal. Found by
      // running it: the device kept a phantom open shift and the dialog closed
      // as though it had worked. Both are worse than the error itself, so the
      // local shift goes and the message reaches the person who tapped.
      await db.shifts.delete(shiftId);
      await outboxRepository.abandon(operationId);
      throw error;
    }
  },

  /**
   * Money into or out of the drawer.
   *
   * Queued like everything else. A pay-out recorded on paper because the
   * terminal had no signal is a pay-out that never makes it into the variance.
   */
  async cash(input: CashMovementInput): Promise<{ queued: boolean }> {
    const idempotencyKey = crypto.randomUUID();
    // One payload for both routes. The direct call takes the shift from the URL
    // and ignores the extra field; the batch upload has no URL and needs it. Two
    // shapes under one idempotency key would fingerprint differently, and the
    // replay would be refused as "already sent with different contents".
    const cashierId = await authRepository.cashierId();
    const payload = {
      ...(cashierId ? { cashier_id: cashierId } : {}),
      movement_type: input.movementType,
      amount: input.amount,
      reason: input.reason,
      shift_id: input.shiftId,
    };

    const operation = await outboxRepository.enqueue({
      type: "cash_movement.create",
      entityId: input.shiftId,
      idempotencyKey,
      payload,
      businessDate: new Date().toISOString().slice(0, 10),
    });

    try {
      await shiftsRepository.cash(input.shiftId, payload, idempotencyKey);
      await outboxRepository.acknowledge(operation.opId);
      return { queued: false };
    } catch (error) {
      if (isTransient(error)) return { queued: true };
      // Refused outright. The money did not move, so nothing is queued.
      await outboxRepository.abandon(operation.opId);
      throw error;
    }
  },

  /**
   * Count the drawer and close.
   *
   * `closed_at` is stamped here, not on arrival: a cash-up done at closing time
   * and uploaded the next morning belongs to the evening it happened.
   */
  async close(input: CashUpInput): Promise<ShiftOutcome> {
    const closedAt = new Date().toISOString();
    const idempotencyKey = crypto.randomUUID();
    const cashierId = await authRepository.cashierId();
    const payload = {
      ...(cashierId ? { cashier_id: cashierId } : {}),
      counted_cash: input.countedCash,
      note: input.note ?? "",
      closed_at: closedAt,
      // See the note in `cash`: the batch upload has no URL to carry the id in,
      // and both routes must send the identical payload.
      shift_id: input.shiftId,
    };

    let operationId = "";
    await db.transaction("rw", db.shifts, db.outbox, db.syncMeta, async () => {
      await db.shifts.update(input.shiftId, {
        status: "CLOSED",
        closedAt,
        countedCash: input.countedCash,
        syncState: "PENDING",
      });
      const operation = await outboxRepository.enqueue({
        type: "shift.close",
        entityId: input.shiftId,
        idempotencyKey,
        payload,
        businessDate: closedAt.slice(0, 10),
      });
      operationId = operation.opId;
    });

    try {
      const shift = await shiftsRepository.close(input.shiftId, payload, idempotencyKey);
      await db.shifts.update(input.shiftId, { syncState: "SYNCED" });
      await outboxRepository.acknowledge(operationId);
      return { shift, queued: false };
    } catch (error) {
      if (!isTransient(error)) {
        // The count itself is kept queued even so. Unlike an open, a cash-up
        // that the server rejects is still a statement somebody made about a
        // drawer they have already counted, and throwing it away would lose the
        // one figure nobody can reconstruct later.
        throw error;
      }
      // No variance to show. It is the server's arithmetic, and the terminal
      // will have it once the queue drains — telling the cashier a figure this
      // device guessed would be worse than telling them to wait.
      const local = await db.shifts.get(input.shiftId);
      return { shift: local ? offlineShift(local) : null, queued: true };
    }
  },

  /**
   * The shift this till is on.
   *
   * Asks the server first, because another terminal on the same till may have
   * opened or closed one. Falls back to what this device knows, which is the
   * whole point of storing it.
   */
  async current(tillId: string): Promise<ShiftOutcome> {
    try {
      const { shift } = await shiftsRepository.current(tillId);
      const local = await reconcile(tillId, shift);

      // This device has cashed up and the count has not uploaded yet. The
      // server still calls the shift open because it has not heard — saying so
      // would put the shift back on the bar and invite a second count of a
      // drawer that has already been counted once.
      if (shift && local?.status === "CLOSED" && local.syncState !== "SYNCED") {
        return { shift: null, queued: true };
      }
      return { shift, queued: false };
    } catch (error) {
      if (!isTransient(error)) throw error;
      const local = await db.shifts.where({ tillId, status: "OPEN" }).first();
      return { shift: local ? offlineShift(local) : null, queued: true };
    }
  },
};

/**
 * A shift the server has not confirmed, shaped like one it has.
 *
 * The figures it cannot know are `null` rather than zero. A drawer showing
 * "R0.00 expected" because the terminal was offline would be a lie a cashier
 * might act on.
 */
function offlineShift(local: LocalShift): Shift {
  return {
    id: local.id,
    status: local.status,
    business_date: local.businessDate,
    opened_at: local.openedAt,
    closed_at: local.closedAt,
    opening_float: local.openingFloat,
    counted_cash: local.countedCash,
    expected_cash: null,
    variance: null,
    sale_count: 0,
    cashier_name: local.cashierName,
    till_name: "",
  };
}

/**
 * Keep the local copy honest about what the server says.
 *
 * A shift closed on another terminal must not keep this one selling into a
 * drawer nobody is counting. The reverse, though, is **not** true: work this
 * device has not managed to upload outranks the server's view, because the
 * server saying "still open" only means it has not heard about the cash-up yet.
 *
 * Found by having a session expire mid-cash-up. The count survived in the queue
 * — that part worked — but the next poll overwrote the local row and the shift
 * reappeared on the bar as open, which is an invitation to count the same drawer
 * twice and file two different figures for it.
 *
 * Returns whatever this device now believes about that shift.
 */
async function reconcile(tillId: string, shift: Shift | null): Promise<LocalShift | undefined> {
  if (shift && shift.status === "OPEN") {
    const existing = await db.shifts.get(shift.id);
    if (existing && existing.syncState !== "SYNCED") return existing;

    await db.shifts.put({
      id: shift.id,
      tillId,
      cashierName: shift.cashier_name,
      businessDate: shift.business_date,
      openedAt: shift.opened_at,
      closedAt: shift.closed_at,
      openingFloat: shift.opening_float,
      countedCash: shift.counted_cash,
      status: "OPEN",
      syncState: "SYNCED",
    });
    return db.shifts.get(shift.id);
  }

  // Nothing open on the server. Anything this device still calls open on that
  // till is stale — but only mark it closed if it has already uploaded, or a
  // shift opened seconds ago on a slow connection would be erased.
  const stale = await db.shifts.where({ tillId, status: "OPEN" }).toArray();
  for (const row of stale) {
    if (row.syncState === "SYNCED") {
      await db.shifts.update(row.id, { status: "CLOSED" });
    }
  }
  return undefined;
}

/**
 * A failure that means "not now", rather than "not ever".
 *
 * The distinction earns its keep: a dead signal means keep the work and retry,
 * while a refusal means the server has judged the request and repeating it will
 * never succeed. Treating the two alike — which is what this did first — left a
 * terminal showing a shift the server had explicitly declined to open.
 */
function isTransient(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  return error instanceof ApiError && error.isRetryable;
}
